import React, { useState, useEffect, useCallback } from "react";

// ============================================================
// 매물 접수함 (관리 탭)
//
// 하는 일:
//   - GET   /api/staff-intake/submissions        접수 목록
//   - POST  /api/staff-intake/submissions/:id/approve  승인 → estate 등록
//   - PATCH /api/staff-intake/submissions/:id    반려 / 메모
//
// 왜 필요한가:
//   일반인 폼(/register)으로 들어온 매물은 estate_submissions 에만 쌓인다.
//   여기서 확인하고 승인해야 실제 매물장(estate)에 올라간다.
//   장난·중복 접수가 매물장을 오염시키지 않도록 하는 장치다.
//
// 연관 파일:
//   backend/routes/publicIntake.js  일반인이 접수하는 쪽
//   backend/routes/staffIntake.js   목록·승인·반려 API
//   pages/PublicIntake.jsx          접수 화면
// ============================================================

// 거래방식 코드 → 한글
const DEAL_LABEL = { month: "월세", jeonse: "전세", sale: "매매" };

// 건물용도 코드 → 한글 (constants/bldLabels.js 와 같은 값)
const KIND_LABEL = {
  "single-room": "원룸", "multi-household": "투룸·다가구", villa: "빌라",
  apt: "아파트", "e-apt": "오피스텔", shop: "상가", office: "사무실", etc: "기타",
};

// 상태 표시
const STATUS = {
  new:       { label: "미처리", color: "#d32f2f", bg: "#fff1f0" },
  reviewing: { label: "검토중", color: "#b35900", bg: "#fff7e6" },
  approved:  { label: "승인",   color: "#1f8a4c", bg: "#f0fff4" },
  rejected:  { label: "반려",   color: "#888",    bg: "#f5f5f5" },
};

// 전화번호에 하이픈 (DB 에는 숫자만 저장되어 있다)
const fmtPhone = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  return d;
};

export default function SubmissionInbox({ cardStyle, titleStyle, subTitleStyle }) {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);   // 처리된 것까지 볼지
  const [msg, setMsg]         = useState("");
  const [denied, setDenied]   = useState(false);   // 권한 없음

  const authFetch = (url, opt = {}) =>
    fetch(url, {
      ...opt,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        ...(opt.headers || {}),
      },
    });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // /api/submissions 는 rt_permissions('submissions') 표를 읽어 등급을 판단한다.
      // 관리탭 체크박스로 조정할 수 있다.
      const r = await authFetch(
        `/api/submissions?status=${showAll ? "all" : "new"}`
      );
      if (r.status === 403) {
        setDenied(true);
        setList([]);
        return;
      }
      const j = await r.json();
      setList(Array.isArray(j.list) ? j.list : []);
    } catch (e) {
      setMsg("목록을 불러오지 못했습니다: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  // ── 승인 → 매물장에 등록 ──
  const approve = async (id) => {
    if (!window.confirm("이 접수를 매물로 등록할까요?")) return;
    setMsg("등록 중...");
    try {
      const r = await authFetch(`/api/submissions/${id}/approve`, {
        method: "POST",
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "승인 실패");
      // 매물 목록(지도 화면)은 이미 불러온 데이터를 들고 있어
      // 승인해도 새 매물이 나타나지 않는다. 페이지를 새로 열어 갱신한다.
      if (window.confirm(
        `매물번호 ${j.estate_no} 로 등록했습니다.\n` +
        `목록으로 이동해 확인하시겠습니까?\n` +
        `(취소하면 접수함에 머무릅니다)`
      )) {
        window.location.href = "/";
        return;
      }
      setMsg(`매물번호 ${j.estate_no} 로 등록했습니다. 상세 정보는 매물 수정에서 채워주세요.`);
      load();
    } catch (e) {
      setMsg("승인 실패: " + e.message);
    }
  };

  // ── 반려 ──
  const reject = async (id) => {
    const reason = window.prompt("반려 사유를 적어주세요 (내부 기록용)");
    if (reason === null) return;
    try {
      const r = await authFetch(`/api/submissions/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "rejected", reject_reason: reason }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error);
      setMsg("반려 처리했습니다.");
      load();
    } catch (e) {
      setMsg("반려 실패: " + e.message);
    }
  };

  // ── 금액 표시 ──
  const priceText = (p) => {
    if (p.deal === "month")  return `보증 ${p.deposit || "-"} / 월 ${p.monthly || "-"}`;
    if (p.deal === "jeonse") return `전세 ${p.jeonse || "-"}`;
    if (p.deal === "sale")   return `매매 ${p.price_sale || "-"}`;
    return "";
  };

  const box = { border: "1px solid #e0e0e0", borderRadius: 10, padding: 14,
                marginBottom: 10, background: "#fff" };
  const btn = { padding: "7px 16px", border: "1px solid #ccc", borderRadius: 6,
                background: "#f5f5f5", cursor: "pointer", fontSize: 14 };
  const newCount = list.filter((s) => s.status === "new").length;

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>
        매물 접수함
        {newCount > 0 && (
          <span style={{ marginLeft: 10, padding: "2px 10px", background: "#d32f2f",
                         color: "#fff", borderRadius: 12, fontSize: 13 }}>
            미처리 {newCount}건
          </span>
        )}
      </div>
      <div style={subTitleStyle}>
        일반인 접수 폼(/register)으로 들어온 매물입니다.
        승인하면 매물장에 등록되며, 상세 정보는 매물 수정에서 채우시면 됩니다.
      </div>

      <div style={{ margin: "12px 0", display: "flex", gap: 8, alignItems: "center" }}>
        <button style={btn} onClick={load}>새로고침</button>
        <label style={{ fontSize: 14, color: "#666", cursor: "pointer" }}>
          <input type="checkbox" checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)} /> 처리된 접수도 보기
        </label>
        {msg && <span style={{ fontSize: 13, color: "#666" }}>{msg}</span>}
      </div>

      {denied && (
        <div style={{ padding: 30, textAlign: "center", color: "#c00",
                      background: "#fff5f5", borderRadius: 8 }}>
          접수물건 승인 권한이 없습니다.<br />
          <span style={{ fontSize: 13, color: "#888" }}>
            관리 → 권한설정에서 '접수물건 승인'을 확인해 주세요.
          </span>
        </div>
      )}

      {loading && <div style={{ padding: 20, color: "#999" }}>불러오는 중...</div>}
      {!loading && list.length === 0 && (
        <div style={{ padding: 30, textAlign: "center", color: "#999" }}>
          {showAll ? "접수 내역이 없습니다." : "미처리 접수가 없습니다."}
        </div>
      )}

      {list.map((s) => {
        const p  = s.payload || {};
        const st = STATUS[s.status] || STATUS.new;
        return (
          <div key={s.id} style={box}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
                          flexWrap: "wrap" }}>
              <span style={{ padding: "2px 10px", borderRadius: 10, fontSize: 12,
                             fontWeight: 700, color: st.color, background: st.bg }}>
                {st.label}
              </span>
              <b style={{ fontSize: 15 }}>{s.owner_name || "이름없음"}</b>
              <a href={`tel:${s.owner_phone}`} style={{ color: "#2b6cb0", fontSize: 14 }}>
                {fmtPhone(s.owner_phone)}
              </a>
              {s.phone_verified === 1 && (
                <span style={{ fontSize: 12, color: "#1f8a4c" }}>본인확인됨</span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: "#999" }}>
                {String(s.created_at).slice(0, 16).replace("T", " ")}
              </span>
            </div>

            <div style={{ fontSize: 15, marginBottom: 4 }}>
              {p.jibunAddress || "(주소 없음)"}
            </div>
            <div style={{ fontSize: 14, color: "#555", marginBottom: 6 }}>
              {KIND_LABEL[p.bld_type] || p.bld_type || "-"} ·{" "}
              {DEAL_LABEL[p.deal] || "-"} · {priceText(p)} (만원)
            </div>
            {p.memo && (
              <div style={{ fontSize: 14, color: "#666", background: "#fafafa",
                            padding: "8px 10px", borderRadius: 6, marginBottom: 8 }}>
                {p.memo}
              </div>
            )}
            {s.estate_no && (
              <div style={{ fontSize: 13, color: "#1f8a4c", marginBottom: 8 }}>
                등록된 매물번호: {s.estate_no}
              </div>
            )}
            {s.reject_reason && (
              <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>
                반려 사유: {s.reject_reason}
              </div>
            )}

            {s.status !== "approved" && (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ ...btn, background: "#1f8a4c", color: "#fff",
                           borderColor: "#1f8a4c", fontWeight: 600 }}
                  onClick={() => approve(s.id)}
                >
                  승인 (매물 등록)
                </button>
                {s.status !== "rejected" && (
                  <button style={{ ...btn, color: "#c00" }} onClick={() => reject(s.id)}>
                    반려
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}